
import type {
  ProductSuggestionItem,
  SuggestionCategoryItem,
  SuggestionItem,
} from '@mg/micro/vue2/SiteSearch'
import { SiteSearch as SiteSearchComponent } from '@mg/micro/vue2/SiteSearch'
import { getAutocompleteGQL } from '@mg/micro/data/gql/search'
import Cookies from 'js-cookie'
import {
  AutocompleteProductInput,
  buildCategoryUrl,
  enhanceProducts,
  getProductImage,
  getUrlPathForHost,
  handleActiveItemKeydown,
  mergeUrlParams,
  stripUrlOrigin,
  ACTIVE_SUGGESTION_LINK_SELECTOR,
  NUMBER_OF_LETTERS_TRIGGERING_AUTOCOMPLETE,
  NUMBER_OF_PRODUCTS_IN_SUGGESTER,
  NUMBER_OF_CATEGORIES_IN_SUGGESTER_MOBILE,
  NUMBER_OF_CATEGORIES_IN_SUGGESTER_DESKTOP,
} from '@mg/micro/vue2'
import { Component, Emit, Prop, Ref, Vue, Watch } from 'vue-property-decorator'
import { AB_TEST_VARIANT, AB_TESTS_NAME } from '~/plugins/abTest'
import { GetAutocompleteArgs } from '~/graphql/gql/search'
import AddToCartButton from '~/components/AddToCartButton.vue'
import {
  GQLAutocompleteResult,
  GQLCategoryAutocomplete,
} from '~/graphql/__generated__/interfaces'
import {
  ADD_LOCAL_SUGGEST_FRAGMENT,
  DELETE_ALL_LOCAL_SUGGEST_FRAGMENTS,
  DELETE_LOCAL_SUGGEST_FRAGMENT,
  GET_LOCAL_SUGGEST_FRAGMENTS,
} from '~/graphql/gqlLocal/localSuggestFragments'
import { GQLLocalHeader } from '~/graphql/__generated__/interfacesLocal'
import { GET_LOCAL_HEADER } from '~/graphql/gqlLocal/localHeader'
import ROUTES from '~/config/routes'
import { Shops } from '~/config/shops'
import { createUrl } from '~/utils/routes'
import { GtmEventTypes } from '~/utils/tracking/gtm'
import showWinterEdition from '~/utils/showWinterEdition'
import {
  DELETE_ALL_LOCAL_SUGGEST_HISTORY_PRODUCTS,
  GET_LOCAL_SUGGEST_HISTORY_PRODUCTS,
} from '~/graphql/gqlLocal/localSuggestHistoryProducts'

export const SEARCH_URL_TRANSLATIONS: Partial<Record<Shops, string>> = {
  [Shops.HR10MA]: 'trazenje',
  [Shops.SI10SI]: 'iskanje',
}

export enum SEARCH_GTM_EVENT_ACTIONS {
  SUBMIT_BY_ENTER = 'Sending query by enter',
  CLEAR_TEXT = 'Clear text',
  SUBMIT_BY_ICON_CLICK = 'Sending query by icon click',
  NAVIGATION_TO_SEARCH = 'Navigation item in whisperer (to search)',
  NAVIGATION_TO_CATEGORY = 'Navigation item in whisperer (to category)',
  NAVIGATION_TO_PRODUCT = 'Navigation item in whisperer (to product)',
  OTHER_RESULTS = 'Button link in whisperer',
  SUGGESTER_CLOSE_CLICK_OUTSIDE = 'Close whisperer by outside click',
  HISTORY_ITEM_REMOVE_CLICK = 'History remove item',
  HISTORY_ITEM_CLICK = 'History click in whisperer',
  HISTORY_PRODUCT_ITEM_CLICK = 'History click in whisperer (product)',
  HISTORY_REMOVE_ALL = 'History remove all',
}

const GTM_SEARCH_BASE_OBJECT = {
  eventCategory: 'Search',
  eventValue: 1,
  eventNonInteractionFlag: true,
}

@Component<HeaderSiteSearch>({
  apollo: {
    getAutocomplete: {
      query: getAutocompleteGQL,
      skip(): boolean {
        const trimmed = this.searchValue?.trim()
        return (
          !trimmed ||
          trimmed.length <= NUMBER_OF_LETTERS_TRIGGERING_AUTOCOMPLETE
        )
      },
      debounce: 300,
      variables(): GetAutocompleteArgs {
        const useMirror = this.$abTesting.searchAbTestMirror()
        const userDbUi = this.userDbUiForQuery

        return {
          text: this.searchValue,
          useMirror,
          userDbUi,
          useDatabreakersSearch: true,
          deviceType: this.$resize.isTablet
            ? 'tablet'
            : this.$resize.isMobile
            ? 'mobile'
            : 'desktop',
        }
      },
    },
    header: {
      query: GET_LOCAL_HEADER,
    },
    suggestFragments: {
      query: GET_LOCAL_SUGGEST_FRAGMENTS,
    },
    suggestHistoryProducts: {
      query: GET_LOCAL_SUGGEST_HISTORY_PRODUCTS,
      skip() {
        return !this.isSuggesterAbTest
      },
    },
  },
  components: {
    AddToCartButton,
    SiteSearchComponent,
  },
})
export default class HeaderSiteSearch extends Vue {
  @Prop(String) currentShop: Shops
  readonly QUERY_SOURCE = { src: 'sug' }
  readonly QUERY_SEARCH_TERM_KEY = 's'

  searchValue = ''
  searchLink = ''

  totalProductResults: number
  inaccurateResult: boolean
  enhancedSuggestionProducts: ProductSuggestionItem[] = []
  enhancedSuggestionCategories: SuggestionCategoryItem[] = []
  enhancedSuggestFragments: SuggestionItem[] = []

  header: GQLLocalHeader
  lastSuggestGAEvent: SEARCH_GTM_EVENT_ACTIONS | string | undefined

  @Ref() siteSearch: typeof SiteSearchComponent

  @Watch('getAutocomplete')
  enhanceApiData(autocompleteData: GQLAutocompleteResult) {
    this.enhancedSuggestionProducts = enhanceProducts(
      autocompleteData?.products as AutocompleteProductInput[],
      {
        currency: this.currency,
        getAvailabilityLabel: this.getAvailabilityLabel,
        buildHref: this.buildSuggestionUrl,
        getImage: getProductImage,
      }
    )

    this.enhancedSuggestionCategories = this.enhanceCategories(
      autocompleteData?.items as GQLCategoryAutocomplete[]
    )

    this.totalProductResults = autocompleteData?.totalProducts ?? 0
    this.inaccurateResult = autocompleteData?.inaccurateResult ?? false
  }

  @Watch('suggestFragments')
  enhanceLocalData(newFragments: string[]) {
    // we reverse the order, to match monolith`s behavior
    this.enhancedSuggestFragments = this.enhanceSuggestFragments(
      newFragments
    ).reverse()
  }

  @Watch('searchValue', { immediate: true })
  updateSearchLink(newVal: string, oldValue: string) {
    /* To hide results from previous query before new query
       is triggered after exceeding requiered number of letters */
    if (
      newVal &&
      newVal.length <= NUMBER_OF_LETTERS_TRIGGERING_AUTOCOMPLETE &&
      newVal !== oldValue
    ) {
      this.enhancedSuggestionProducts = []
      this.enhancedSuggestionCategories = []
    }

    this.searchLink = this.createSearchLink()
  }

  @Watch('inaccurateResult', { immediate: true })
  updateInaccurateResult(newVal: boolean) {
    this.inaccurateResult = newVal
  }

  get clientWatchParamsS() {
    if (!process.client) {
      return null
    }

    return this.$route.query
  }

  @Watch('clientWatchParamsS', { immediate: true })
  syncSearchTerm() {
    const searchTermFromUrl = (this.$route.query?.s as string) ?? ''
    if (searchTermFromUrl !== this.searchValue) {
      this.searchValue = searchTermFromUrl
    }
  }

  trackSuggesterUserEventGTM(
    eventAction: SEARCH_GTM_EVENT_ACTIONS | string,
    eventLabel: string
  ) {
    if (this.lastSuggestGAEvent !== SEARCH_GTM_EVENT_ACTIONS.SUBMIT_BY_ENTER) {
      this.$pushAnalyticsEvent(GtmEventTypes.EventFire, {
        ...GTM_SEARCH_BASE_OBJECT,
        eventAction,
        eventLabel: eventLabel.trim().toLowerCase(),
      })
    }

    this.lastSuggestGAEvent = eventAction
  }

  @Emit()
  onClickOutside() {
    const searchText = this.searchValue?.trim().toLowerCase() || ''
    this.trackSuggesterUserEventGTM(
      SEARCH_GTM_EVENT_ACTIONS.SUGGESTER_CLOSE_CLICK_OUTSIDE,
      searchText
    )
  }

  get abTestVariant() {
    return this.$abTesting.getABtestVariantSimple(
      AB_TESTS_NAME.SEARCH_SUGGESTER
    )
  }

  get translations() {
    return {
      delete: this.$t('common.frm_attachment_delete'),
      history_delete: this.isSuggesterAbTest
        ? this.$t('common.suggesterClearHistory')
        : this.$t('common.clearFragmentHistory'),
      history_heading: this.$t('common.lastSearched'),
      more: this.$t('common.next_results'),
      search_action: this.$t('common.cta_search'),
      search_inaccurate_results: this.$t('common.search_inaccurate_results'),
    }
  }

  get isWinterEdition() {
    return showWinterEdition(this.$shopConfig.webConfig)
  }

  mounted() {
    if (process.client) {
      this.searchValue =
        (this.$route.query[this.QUERY_SEARCH_TERM_KEY] as string) ?? ''

      this.$nextTick(() => {
        const el = (this.$refs.siteSearch as any)?.$el
        if (el) {
          el.addEventListener(
            'keydown',
            (event: KeyboardEvent) =>
              handleActiveItemKeydown(event, ACTIVE_SUGGESTION_LINK_SELECTOR),
            true
          )
        }
      })
    }
  }

  beforeDestroy() {
    const el = (this.$refs.siteSearch as any)?.$el
    if (el) {
      el.removeEventListener(
        'keydown',
        (event: KeyboardEvent) =>
          handleActiveItemKeydown(event, ACTIVE_SUGGESTION_LINK_SELECTOR),
        true
      )
    }
  }

  createSearchLink(searchTerm?: string, addQuerySource = true) {
    const query = {
      ...(addQuerySource && this.QUERY_SOURCE),
      [this.QUERY_SEARCH_TERM_KEY]: searchTerm || this.searchValue,
    }

    return createUrl(ROUTES.search, this.currentShop, { query })
  }

  buildSuggestionUrl(url: string) {
    const urlWithoutDomain = url
      .split('/')
      .filter((_item, index) => index >= 3)
      .join('/')

    return mergeUrlParams(`/${urlWithoutDomain}`, {
      ...this.QUERY_SOURCE,
      [this.QUERY_SEARCH_TERM_KEY]: this.searchValue,
    })
  }

  categoryClick(searchTerm: string, _: any, url: string, index: number) {
    if (!searchTerm) {
      return
    }

    this.searchValue = searchTerm
    this.addFragment()

    const searchTermWithPosition = `${index + 1}|${searchTerm}`
      .trim()
      .toLowerCase()
    const urlTranslation = SEARCH_URL_TRANSLATIONS[this.$shopConfig.shopId]

    urlTranslation && url?.includes(urlTranslation)
      ? this.trackSuggesterUserEventGTM(
          SEARCH_GTM_EVENT_ACTIONS.NAVIGATION_TO_SEARCH,
          searchTermWithPosition
        )
      : this.trackSuggesterUserEventGTM(
          SEARCH_GTM_EVENT_ACTIONS.NAVIGATION_TO_CATEGORY,
          searchTermWithPosition
        )
  }

  historyClick(searchTerm: string) {
    if (!searchTerm) {
      return
    }
    this.trackSuggesterUserEventGTM(
      SEARCH_GTM_EVENT_ACTIONS.HISTORY_ITEM_CLICK,
      searchTerm.trim().toLowerCase()
    )
  }

  historyProductClick(searchProduct: string) {
    if (!searchProduct) {
      return
    }
    this.trackSuggesterUserEventGTM(
      SEARCH_GTM_EVENT_ACTIONS.HISTORY_PRODUCT_ITEM_CLICK,
      searchProduct.trim().toLowerCase()
    )
  }

  get currency() {
    return this.$shopConfig.webConfig.currency
  }

  get isSuggesterAbTest() {
    return (
      this.abTestVariant === AB_TEST_VARIANT.B ||
      this.abTestVariant === AB_TEST_VARIANT.C
    )
  }

  get userDbUiForQuery() {
    return this.$shopConfig.abTests.cookies.search.dbUi ?? Cookies.get('db_ui')
  }

  get suggestionProducts() {
    return this.enhancedSuggestionProducts.slice(
      0,
      NUMBER_OF_PRODUCTS_IN_SUGGESTER
    )
  }

  get suggestionCategories() {
    const count = this.$resize.isDesktop
      ? NUMBER_OF_CATEGORIES_IN_SUGGESTER_DESKTOP
      : NUMBER_OF_CATEGORIES_IN_SUGGESTER_MOBILE

    return this.enhancedSuggestionCategories.slice(0, count)
  }

  enhanceCategories(
    categories: GQLCategoryAutocomplete[] = []
  ): SuggestionCategoryItem[] {
    return categories.map(
      ({ urlKey, title, id, imageUrl, type, brand, url, __typename }) => {
        const safeUrlKey = stripUrlOrigin(urlKey)

        const href = buildCategoryUrl(
          getUrlPathForHost(url, window.location.host),
          safeUrlKey,
          title,
          type,
          {
            brandId: brand?.id,
            brandUrl: ROUTES.brand.paths[this.$shopConfig.shopId],
          }
        )

        return {
          href,
          id,
          ...(imageUrl && { imageUrl }),
          title,
          urlKey,
          __typename,
        }
      }
    )
  }

  stripTags(title: string) {
    if (!title) {
      return ''
    }
    return title.replace(/(<([^>]+)>)/gi, '')
  }

  enhanceSuggestFragments(fragments: string[]) {
    const enhancedFragments = fragments.map((fragment) => {
      return {
        href: this.createSearchLink(fragment),
        title: fragment,
      }
    })

    return this.isSuggesterAbTest
      ? window.innerWidth > 1024
        ? enhancedFragments.slice(-7)
        : enhancedFragments.slice(-4)
      : enhancedFragments
  }

  getAvailabilityLabel(availabilityKey?: string | null | undefined) {
    const keyWithFallback = availabilityKey || 'avail-unknown'

    const translationKey = `common.${keyWithFallback}`
    return this.$t(translationKey) as string
  }

  deleteFragment(fragment: string) {
    this.trackSuggesterUserEventGTM(
      SEARCH_GTM_EVENT_ACTIONS.HISTORY_ITEM_REMOVE_CLICK,
      fragment.trim().toLowerCase()
    )

    this.$apollo.mutate({
      mutation: DELETE_LOCAL_SUGGEST_FRAGMENT,
      variables: {
        fragment,
      },
    })
  }

  clearFragments() {
    this.$apollo.mutate({
      mutation: DELETE_ALL_LOCAL_SUGGEST_FRAGMENTS,
    })

    if (this.isSuggesterAbTest) {
      this.$apollo.mutate({
        mutation: DELETE_ALL_LOCAL_SUGGEST_HISTORY_PRODUCTS,
      })
    }

    this.trackSuggesterUserEventGTM(
      SEARCH_GTM_EVENT_ACTIONS.HISTORY_REMOVE_ALL,
      '-'
    )
  }

  addFragment() {
    this.$apollo.mutate({
      mutation: ADD_LOCAL_SUGGEST_FRAGMENT,
      variables: {
        fragment: this.searchValue,
      },
    })
  }

  get searchValueForGTM() {
    return this.searchValue ? this.searchValue.trim().toLowerCase() : ''
  }

  submit(isSubmitByButton: boolean = false) {
    typeof isSubmitByButton === 'boolean'
      ? this.trackSuggesterUserEventGTM(
          SEARCH_GTM_EVENT_ACTIONS.SUBMIT_BY_ICON_CLICK,
          this.searchValueForGTM
        )
      : this.trackSuggesterUserEventGTM(
          SEARCH_GTM_EVENT_ACTIONS.SUBMIT_BY_ENTER,
          this.searchValueForGTM
        )

    this.addFragment()
    const searchLinkWithoutSourceParam = this.createSearchLink(undefined, false)
    this.$router.push(searchLinkWithoutSourceParam)
  }

  searchClick(translationText: string) {
    const eventAction = `${
      SEARCH_GTM_EVENT_ACTIONS.OTHER_RESULTS
    } (${translationText
      .replace(/[0-9]/g, '')
      .split(' ')
      .filter(Boolean)
      .join(' ')})`

    this.trackSuggesterUserEventGTM(eventAction, this.searchValueForGTM)
  }

  itemClick(title: string, index: number) {
    const position = this.enhancedSuggestionCategories.length + index + 1
    const titleWihtoutTags = this.stripTags(title)
    const titleWithPosition = `${position}|${titleWihtoutTags}`

    this.trackSuggesterUserEventGTM(
      SEARCH_GTM_EVENT_ACTIONS.NAVIGATION_TO_PRODUCT,
      titleWithPosition
    )
  }

  clearSearchTerm() {
    this.trackSuggesterUserEventGTM(
      SEARCH_GTM_EVENT_ACTIONS.CLEAR_TEXT,
      this.searchValueForGTM
    )
    this.searchValue = ''
  }

  trackAbTest() {
    this.$abTesting.trackActiveABTest(AB_TESTS_NAME.SEARCH_SUGGESTER, true)
  }
}
